"use client";

import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { LINEAR_REGRESSION_CALCULATOR_STRINGS } from "@/components/tools/linear-regression-calculator";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import { t } from "@/lib/i18n/interpolate";
import {
  formatPValue,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import type { RegressionReport } from "@/lib/tool-kit/stats-descriptive/regression";

/*
 * The tables and working under the linear-regression widget, split out so the widget
 * file stays readable. Every word comes from the widget's own strings.
 */

type Strings = typeof LINEAR_REGRESSION_CALCULATOR_STRINGS;

interface OutputProps {
  report: RegressionReport;
  fmt: NumberFormatter;
  strings: Strings;
}

/** "ŷ = 8.9895 + 2.3684x", with the sign of the slope folded into the operator. */
export function regressionEquation(
  report: RegressionReport,
  fmt: NumberFormatter,
  strings: Strings
): string {
  const a = formatStat(fmt, report.intercept);
  const b = formatStat(fmt, Math.abs(report.slope));
  return report.slope < 0
    ? t(strings.equationMinus, { a, b })
    : t(strings.equationPlus, { a, b });
}

export function CoefficientsTable({ report, fmt, strings }: OutputProps) {
  const num = (value: number) => formatStat(fmt, value);
  const interval = ([low, high]: [number, number]) =>
    t(strings.ciTemplate, { low: num(low), high: num(high) });
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.coefficientsTitle}</h3>
      <ResultTable
        head={[
          strings.colTerm,
          strings.colEstimate,
          strings.colSe,
          strings.colT,
          strings.colP,
          strings.colCi,
        ]}
        rows={[
          [
            strings.termIntercept,
            num(report.intercept),
            num(report.seIntercept),
            num(report.tIntercept),
            formatPValue(fmt, report.pIntercept),
            interval(report.interceptCi),
          ],
          [
            strings.termSlope,
            num(report.slope),
            num(report.seSlope),
            num(report.tSlope),
            formatPValue(fmt, report.pSlope),
            interval(report.slopeCi),
          ],
        ]}
      />
    </div>
  );
}

export function AnovaTable({ report, fmt, strings }: OutputProps) {
  const num = (value: number) => formatStat(fmt, value);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.anovaTitle}</h3>
      <ResultTable
        footer={[
          strings.sourceTotal,
          fmt(report.n - 1),
          num(report.sst),
          "",
          "",
          "",
        ]}
        head={[
          strings.colSource,
          strings.colDf,
          strings.colSs,
          strings.colMs,
          strings.colF,
          strings.colP,
        ]}
        rows={[
          [
            strings.sourceRegression,
            fmt(report.dfRegression),
            num(report.ssr),
            num(report.msr),
            num(report.f),
            formatPValue(fmt, report.pF),
          ],
          [
            strings.sourceResidual,
            fmt(report.dfResidual),
            num(report.sse),
            num(report.mse),
            "",
            "",
          ],
        ]}
      />
    </div>
  );
}

export function ResidualsTable({ report, fmt, strings }: OutputProps) {
  const num = (value: number) => formatStat(fmt, value);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.residualsTitle}</h3>
      <ResultTable
        head={[
          strings.colIndex,
          strings.colX,
          strings.colY,
          strings.colFitted,
          strings.colResidual,
        ]}
        rows={report.residuals.map((row, index) => [
          fmt(index + 1),
          num(row.x),
          num(row.y),
          num(row.fitted),
          num(row.residual),
        ])}
      />
    </div>
  );
}

export function RegressionWorking({ report, fmt, strings }: OutputProps) {
  const num = (value: number) => formatStat(fmt, value);
  const shared = {
    mx: num(report.meanX),
    my: num(report.meanY),
    sxx: num(report.sxx),
    sxy: num(report.sxy),
    syy: num(report.syy),
    b: num(report.slope),
    a: num(report.intercept),
    df: fmt(report.dfResidual),
  };
  return (
    <WorkingPanel title={strings.workingTitle}>
      <Formula>{t(strings.meansLine, shared)}</Formula>
      <Formula>{t(strings.sumsLine, shared)}</Formula>
      <Formula>{t(strings.slopeLine, shared)}</Formula>
      <Formula>{t(strings.interceptLine, shared)}</Formula>
      <Formula>
        {t(strings.rLine, {
          r: num(report.r),
          ssr: num(report.ssr),
          sst: num(report.sst),
          r2: num(report.rSquared),
        })}
      </Formula>
      <Formula>
        {t(strings.seLine, {
          ...shared,
          sse: num(report.sse),
          s: num(report.see),
        })}
      </Formula>
      <Formula>
        {t(strings.seSlopeLine, {
          ...shared,
          s: num(report.see),
          seb: num(report.seSlope),
          t: num(report.tSlope),
        })}
      </Formula>
      <Explanation>{strings.assumptionsNote}</Explanation>
    </WorkingPanel>
  );
}
